import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Radio, Video } from 'lucide-react';
import { useFullscreen } from '../contexts/FullscreenContext';

const VIDEO_ALARM_CONTAINER_ID = 'alarm-modal-youtube-player';

export default function AlarmModal({
  stopAlarm,
  snoozeAlarm,
  alarmType = 'basic',
  videoAlarm = null,
  timerVideoAlarm = null,
  basicAlarm = null,
  radioAlarm = null
}) {
  const { isFullscreen } = useFullscreen();
  const playerWrapperRef = useRef(null);
  const videoId = videoAlarm?.videoId;
  const createPlayer = videoAlarm?.createYouTubePlayer;
  const stopPlayer = videoAlarm?.stopVideo;
  const manualStartPlay = videoAlarm?.manualStartPlay;
  const [showSnoozeConfirm, setShowSnoozeConfirm] = useState(false);
  const [snoozeNewTime, setSnoozeNewTime] = useState('');

  useEffect(() => {
    if (
      alarmType === 'video' &&
      videoId &&
      typeof createPlayer === 'function' &&
      playerWrapperRef.current
    ) {
      const container = playerWrapperRef.current;
      createPlayer(container, { allowFullscreen: true, autoplay: 1 });

      return () => {
        stopPlayer?.();
      };
    }
  }, [alarmType, videoId, createPlayer, stopPlayer, isFullscreen]);

  const handleManualVideoPlay = () => {
    if (!manualStartPlay) return;
    manualStartPlay(playerWrapperRef.current || VIDEO_ALARM_CONTAINER_ID);
  };

  const handleSnooze = () => {
    // 获取当前闹钟数据
    const alarm = basicAlarm || radioAlarm || videoAlarm;
    if (!alarm) {
      snoozeAlarm();
      return;
    }

    // 计算新的闹钟时间
    const currentHour = parseInt(alarm.alarmHour);
    const currentMinute = parseInt(alarm.alarmMinute);
    const snoozeMins = parseInt(alarm.snoozeMinutes);
    
    const newMinute = (currentMinute + snoozeMins) % 60;
    const newHour = (currentHour + Math.floor((currentMinute + snoozeMins) / 60)) % 24;
    const newTime = `${newHour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
    
    setSnoozeNewTime(newTime);
    setShowSnoozeConfirm(true);
    
    // 先显示确认信息2秒，然后执行 snooze
    setTimeout(() => {
      snoozeAlarm();
      // 再延迟0.5秒后关闭确认信息，让用户看到状态变化
      setTimeout(() => {
        setShowSnoozeConfirm(false);
      }, 500);
    }, 2000);
  };

  const renderVideoAlarmContent = () => (
    <div>
      {/* 标题栏 */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Video className="w-6 h-6 text-white" />
          <div>
            <h3 className="text-xl font-bold text-white">Video Alarm</h3>
            {videoAlarm?.videoTitle && (
              <p className="text-red-100 text-sm">{videoAlarm.videoTitle}</p>
            )}
          </div>
        </div>
      </div>

      <div className="text-center">
      {videoAlarm?.videoId ? (
        <div className="mb-10 space-y-4">
          <div className="bg-black rounded-xl overflow-hidden">
            <div
              ref={playerWrapperRef}
              id={`${VIDEO_ALARM_CONTAINER_ID}-${isFullscreen ? 'fullscreen' : 'inline'}`}
              className="w-full aspect-video bg-black"
              data-youtube-placeholder="true"
            />
          </div>

          {videoAlarm.showPlayPrompt && (
            <div className="bg-white/10 rounded-xl text-white p-6 space-y-4">
              <p className="text-lg">⚠️ 浏览器阻止了自动播放</p>
              <p className="text-sm text-white/80">
                由于浏览器策略限制，请点击下方按钮手动开始播放视频。
              </p>
              <button
                onClick={handleManualVideoPlay}
                className="px-8 py-3 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                🎵 手动播放视频
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="mb-10 p-6 bg-white/10 rounded-xl text-white space-y-2">
          <p className="text-lg">⚠️ 尚未设置视频闹钟</p>
          <p className="text-sm text-white/80">
            请在视频闹钟页面选择或输入一个 YouTube 视频链接。
          </p>
        </div>
      )}

      <div className="flex justify-center">
        <button
          onClick={stopAlarm}
          className="px-12 py-4 bg-white text-red-600 rounded-xl font-bold text-xl hover:bg-gray-100 transition-colors"
        >
          Stop
        </button>
      </div>
      </div>
    </div>
  );

  const renderTimerVideoAlarmContent = () => (
    <div className="text-center">
      <Video className="w-24 h-24 text-white mx-auto mb-6 animate-bounce" />
      <h3 className="text-5xl font-bold text-white mb-8">TIMER VIDEO ALARM!</h3>

      {timerVideoAlarm?.videoId && (
        <div className="mb-8 bg-black rounded-xl overflow-hidden">
          <div className="w-full h-64 flex items-center justify-center">
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${timerVideoAlarm.videoId}?autoplay=1&controls=1&enablejsapi=1&origin=${window.location.origin}`}
              title={timerVideoAlarm.videoTitle || 'Timer Video Alarm'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
          {timerVideoAlarm.videoTitle && (
            <div className="p-2 bg-white/10 text-white text-center">
              <p className="text-sm">{timerVideoAlarm.videoTitle}</p>
            </div>
          )}
        </div>
      )}

      {!timerVideoAlarm?.videoId && (
        <div className="mb-8 p-8 bg-white/10 rounded-xl">
          <p className="text-white text-lg mb-4">⚠️ 没有设置视频</p>
          <p className="text-white/80 text-sm">
            请在定时器设置中启用视频闹钟并输入YouTube视频ID。
          </p>
        </div>
      )}

      <div className="space-y-4">
        {timerVideoAlarm?.videoId && (
          <button
            onClick={() => {
              // 尝试手动播放视频
              const iframe = document.querySelector('iframe[src*="youtube.com"]');
              if (iframe) {
                // 重新加载iframe以触发播放
                const src = iframe.src;
                iframe.src = '';
                setTimeout(() => {
                  iframe.src = src;
                }, 100);
              }
            }}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
          >
            🔊 手动播放视频
          </button>
        )}
        
        <button
          onClick={stopAlarm}
          className="px-12 py-4 bg-white text-red-600 rounded-xl font-bold text-xl hover:bg-gray-100 transition-colors"
        >
          Stop Timer
        </button>
      </div>
    </div>
  );

  const renderTimerAlarmContent = () => (
    <div className="text-center">
      <Volume2 className="w-24 h-24 text-white mx-auto mb-6 animate-bounce" />
      <h3 className="text-5xl font-bold text-white mb-8">TIMER ALARM!</h3>
      <button
        onClick={stopAlarm}
        className="px-12 py-4 bg-white text-red-600 rounded-xl font-bold text-xl hover:bg-gray-100 transition-colors"
      >
        Stop Timer
      </button>
    </div>
  );

  const renderRadioAlarmContent = () => {
    if (showSnoozeConfirm) {
      return (
        <div className="text-center">
          <Radio className="w-24 h-24 text-white mx-auto mb-6" />
          <h3 className="text-4xl font-bold text-white mb-4">Snooze Activated</h3>
          <p className="text-2xl text-white/90 mb-8">
            Alarm rescheduled for {snoozeNewTime}
          </p>
        </div>
      );
    }
    
    return (
      <div className="text-center">
        <Radio className="w-24 h-24 text-white mx-auto mb-6 animate-bounce" />
        <h3 className="text-4xl font-bold text-white mb-8">RADIO ALARM!</h3>
        <div className="flex gap-4 justify-center">
          <button
            onClick={stopAlarm}
            className="px-12 py-4 bg-white text-red-600 rounded-xl font-bold text-xl hover:bg-gray-100 transition-colors"
          >
            Stop
          </button>
          <button
            onClick={handleSnooze}
            className="px-12 py-4 bg-yellow-500 text-white rounded-xl font-bold text-xl hover:bg-yellow-600 transition-colors"
          >
            Snooze
          </button>
        </div>
      </div>
    );
  };

  const renderBasicAlarmContent = () => {
    if (showSnoozeConfirm) {
      return (
        <div className="text-center">
          <Volume2 className="w-24 h-24 text-white mx-auto mb-6" />
          <h3 className="text-4xl font-bold text-white mb-4">Snooze Activated</h3>
          <p className="text-2xl text-white/90 mb-8">
            Alarm rescheduled for {snoozeNewTime}
          </p>
        </div>
      );
    }
    
    return (
      <div className="text-center">
        <Volume2 className="w-24 h-24 text-white mx-auto mb-6 animate-bounce" />
        <h3 className="text-4xl font-bold text-white mb-8">ALARM!</h3>
        <div className="flex gap-4 justify-center">
          <button
            onClick={stopAlarm}
            className="px-12 py-4 bg-white text-red-600 rounded-xl font-bold text-xl hover:bg-gray-100 transition-colors"
          >
            Stop
          </button>
          <button
            onClick={handleSnooze}
            className="px-12 py-4 bg-yellow-500 text-white rounded-xl font-bold text-xl hover:bg-yellow-600 transition-colors"
          >
            Snooze
          </button>
        </div>
      </div>
    );
  };

  const renderAlarmContent = () => {
    switch (alarmType) {
      case 'video':
        return renderVideoAlarmContent();
      case 'timer-video':
        return renderTimerVideoAlarmContent();
      case 'timer':
        return renderTimerAlarmContent();
      case 'radio':
        return renderRadioAlarmContent();
      default:
        return renderBasicAlarmContent();
    }
  };

  // 当 alarmRinging 变为 false 时，重置 snooze 确认状态
  useEffect(() => {
    if (!showSnoozeConfirm) return;
    
    const checkAlarmStatus = setInterval(() => {
      const alarm = basicAlarm || radioAlarm || videoAlarm;
      if (alarm && !alarm.alarmRinging) {
        // 如果 alarmRinging 变为 false，延迟关闭确认信息
        setTimeout(() => {
          setShowSnoozeConfirm(false);
        }, 500);
      }
    }, 100);
    
    return () => clearInterval(checkAlarmStatus);
  }, [showSnoozeConfirm, basicAlarm, radioAlarm, videoAlarm]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`bg-blue-600 rounded-3xl p-12 shadow-2xl border-4 border-blue-400 max-w-4xl w-full mx-4 ${showSnoozeConfirm ? '' : 'animate-pulse'}`}>
        {renderAlarmContent()}
      </div>
    </div>
  );
}
